import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCRUD } from "../../../api/crud";
import { useBreadcrumbs } from "../../../components/ui/breadcrumps/breadcrumbsContext";
import CustomCircularProgress from "../../../components/ui/customCircularProgress/customCircularProgress.component";
import {
  INVOICES_ENDPOINT,
  INVOICE_PAYMENT_METHOD_LABEL_KEYS,
  INVOICE_STATUS_COLOR_MAP,
  INVOICE_STATUS_LABEL_KEYS,
  KSEF_STATUS_COLOR_MAP,
  KSEF_STATUS_LABEL_KEYS,
  RECEIVER_ROLE_LABEL_KEYS,
  VAT_RATE_LABEL_KEYS,
} from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { Invoice } from "../../../types/invoices";
import { formatNumber } from "../../../utils";

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Box>
);

export default function ViewInvoice() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { get } = useCRUD(INVOICES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const canViewInvoices = useHasPermission("invoice.view_invoice");

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      return;
    }
    const response = await get(id);
    const fetched = response?.data ?? null;
    setInvoice(fetched);
    setLoading(false);

    // The breadcrumb would otherwise show the raw row id. A draft has no
    // number yet, so it falls back to the buyer - same rule as the heading.
    setOverride(
      `/invoices/${id}`,
      fetched ? fetched.number || fetched.buyer_name : ""
    );
  }, [id, get, setOverride]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // The toolbar issues, sends and files the invoice; it announces the change
  // rather than owning the data, so this view refetches on the same event.
  useEffect(() => {
    window.addEventListener("invoiceUpdated", fetchInvoice);
    return () => window.removeEventListener("invoiceUpdated", fetchInvoice);
  }, [fetchInvoice]);

  if (!canViewInvoices) {
    return null;
  }

  if (loading) {
    return <CustomCircularProgress />;
  }

  if (!invoice) {
    return <Typography>{t("Invoice not found")}</Typography>;
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        {/* A draft has no number yet, so it falls back to the buyer - the most
            identifying thing it has. Never "Draft": the status chip beside this
            already says that. */}
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {t("Invoice")} - {invoice.number || invoice.buyer_name}
        </Typography>
        <Chip
          size="small"
          label={t(INVOICE_STATUS_LABEL_KEYS[invoice.status] ?? invoice.status)}
          color={INVOICE_STATUS_COLOR_MAP[invoice.status] ?? "default"}
        />
        <Chip
          size="small"
          color={invoice.is_paid ? "success" : "warning"}
          label={invoice.is_paid ? t("Paid") : t("Awaiting payment")}
        />
        <Chip
          size="small"
          label={`KSeF: ${t(KSEF_STATUS_LABEL_KEYS[invoice.ksef_status] ?? invoice.ksef_status)}`}
          color={KSEF_STATUS_COLOR_MAP[invoice.ksef_status] ?? "default"}
        />
        {invoice.is_overdue && (
          <Chip size="small" color="error" label={t("Overdue")} />
        )}
      </Stack>

      {invoice.ksef_error && (
        <Alert severity="error">
          {t("KSeF rejected this invoice")}: {invoice.ksef_error}
        </Alert>
      )}

      {invoice.ksef_number && (
        <Alert severity="success">
          {t("KSeF number")}: {invoice.ksef_number}
        </Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("Seller")}
          </Typography>
          <Typography variant="subtitle2">{invoice.seller_name}</Typography>
          <Typography variant="body2">{invoice.seller_street}</Typography>
          <Typography variant="body2">
            {invoice.seller_zip_code} {invoice.seller_city}
          </Typography>
          {invoice.seller_nip && (
            <Typography variant="body2">NIP: {invoice.seller_nip}</Typography>
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("Buyer")}
          </Typography>
          <Typography variant="subtitle2">{invoice.buyer_name}</Typography>
          <Typography variant="body2">{invoice.buyer_street}</Typography>
          <Typography variant="body2">
            {invoice.buyer_zip_code} {invoice.buyer_city}
          </Typography>
          {invoice.buyer_nip && (
            <Typography variant="body2">NIP: {invoice.buyer_nip}</Typography>
          )}
        </Paper>
        {invoice.receiver_role && (
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t("Receiver")}
            </Typography>
            <Typography variant="subtitle2">{invoice.receiver_name}</Typography>
            <Typography variant="body2">{invoice.receiver_street}</Typography>
            <Typography variant="body2">
              {invoice.receiver_zip_code} {invoice.receiver_city}
            </Typography>
            {invoice.receiver_nip && (
              <Typography variant="body2">
                NIP: {invoice.receiver_nip}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {t(
                RECEIVER_ROLE_LABEL_KEYS[invoice.receiver_role] ??
                  invoice.receiver_role
              )}
            </Typography>
          </Paper>
        )}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
        <Field label={t("Date of issue")} value={invoice.issued_at} />
        <Field label={t("Date of sale")} value={invoice.sold_at} />
        <Field label={t("Payment due")} value={invoice.due_date} />
        <Field
          label={t("Payment method")}
          value={t(
            INVOICE_PAYMENT_METHOD_LABEL_KEYS[invoice.payment_method] ??
              invoice.payment_method
          )}
        />
        {invoice.paid_at && (
          <Field label={t("Paid at")} value={invoice.paid_at} />
        )}
      </Stack>

      <Divider />

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("No.")}</TableCell>
            <TableCell>{t("Name")}</TableCell>
            <TableCell align="right">{t("Qty")}</TableCell>
            <TableCell>{t("Unit of measure")}</TableCell>
            <TableCell align="right">{t("Net price")}</TableCell>
            <TableCell align="right">{t("Net value")}</TableCell>
            <TableCell align="right">{t("VAT")}</TableCell>
            <TableCell align="right">{t("VAT amount")}</TableCell>
            <TableCell align="right">{t("Gross value")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoice.items_detail.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.ordinal}</TableCell>
              <TableCell>{item.name}</TableCell>
              <TableCell align="right">{formatNumber(item.quantity)}</TableCell>
              <TableCell>{item.unit}</TableCell>
              <TableCell align="right">
                {formatNumber(item.price_net)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(item.total_net)}
              </TableCell>
              <TableCell align="right">
                {t(VAT_RATE_LABEL_KEYS[item.vat_rate] ?? item.vat_rate)}
              </TableCell>
              <TableCell align="right">
                {formatNumber(item.total_vat)}
              </TableCell>
              <TableCell align="right">{formatNumber(item.total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("VAT summary")}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("VAT rate")}</TableCell>
                <TableCell align="right">{t("Net")}</TableCell>
                <TableCell align="right">{t("VAT")}</TableCell>
                <TableCell align="right">{t("Gross")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.vat_summary.map((row) => (
                <TableRow key={row.vat_rate}>
                  <TableCell>
                    {t(VAT_RATE_LABEL_KEYS[row.vat_rate] ?? row.vat_rate)}
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.net)}</TableCell>
                  <TableCell align="right">{formatNumber(row.vat)}</TableCell>
                  <TableCell align="right">{formatNumber(row.gross)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, minWidth: 260 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t("Net total")}
            </Typography>
            <Typography variant="body2">
              {formatNumber(invoice.total_net)} {invoice.currency}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t("VAT total")}
            </Typography>
            <Typography variant="body2">
              {formatNumber(invoice.total_vat)} {invoice.currency}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2">{t("To pay")}</Typography>
            <Typography variant="subtitle2">
              {formatNumber(invoice.total)} {invoice.currency}
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      {invoice.sent_to_client_at && (
        <Typography variant="caption" color="text.secondary">
          {t("Sent to the client at {{date}} ({{email}})", {
            date: new Date(invoice.sent_to_client_at).toLocaleString(),
            email: invoice.sent_to_email,
          })}
        </Typography>
      )}

      {invoice.notes && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            {t("Notes")}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {invoice.notes}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
